const MS_PER_DAY = 86400000;
const DAYS_PER_YEAR = 365;

function parseDate(value) {
  const d = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    throw new Error(`Invalid date: ${value}`);
  }
  return d;
}

function xnpv(rate, flows) {
  const t0 = flows[0].date.getTime();
  return flows.reduce((sum, row) => {
    const days = (row.date.getTime() - t0) / MS_PER_DAY;
    return sum + row.amount / (1 + rate) ** (days / DAYS_PER_YEAR);
  }, 0);
}

/** Excel-compatible XIRR for irregular dated cash flows. */
export function computeXirr(dates, amounts, guess = 0.1) {
  if (!dates?.length || dates.length !== amounts?.length) {
    throw new Error('Dates and amounts must have the same non-zero length');
  }
  if (dates.length < 2) {
    throw new Error('XIRR needs at least two cash flows');
  }

  const flows = dates.map((date, i) => ({
    date: parseDate(date),
    amount: Number(amounts[i]),
  }));

  if (!flows.some((f) => f.amount > 0) || !flows.some((f) => f.amount < 0)) {
    throw new Error('XIRR needs at least one positive and one negative cash flow');
  }

  flows.sort((a, b) => a.date - b.date);

  let rate = guess;
  for (let i = 0; i < 100; i += 1) {
    const f = xnpv(rate, flows);
    const eps = 1e-6;
    const f1 = xnpv(rate + eps, flows);
    const deriv = (f1 - f) / eps;
    if (Math.abs(deriv) < 1e-12) break;
    const next = rate - f / deriv;
    if (!Number.isFinite(next)) break;
    if (Math.abs(next - rate) < 1e-9) {
      return next;
    }
    rate = next;
  }

  throw new Error('XIRR did not converge — try a different guess or check cash flows');
}

export function daysUntil(dateStr) {
  const target = parseDate(dateStr);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const targetUtc = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
  return Math.round((targetUtc - todayUtc) / MS_PER_DAY);
}

export function addMonths(dateStr, months) {
  const d = parseDate(dateStr);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}
